import {
  STREAM_CHUNK_SIZE,
  TERRAIN_VERTEX_SPACING,
  getWorldHeightAt,
  getWorldRiverProfileAt,
  getWorldTerrainSurfaceWeights,
  sampleWorldWaterAt,
  WORLD_WATER_HALF_WIDTH_FACTOR,
} from "@endclock/shared";
import type { Camera } from "../engine/camera.js";
import type { MeshGeometry } from "../engine/mesh.js";
import { GpuMesh, type Renderer } from "../engine/renderer.js";
import {
  CHUNK_EVICTION_RADIUS,
  CHUNK_PREFETCH_RADIUS,
  TERRAIN_GPU_RADIUS,
  chunkDistance,
  prioritizedChunkWindow,
  worldChunkAt,
  worldChunkId,
} from "../world/chunk-streaming.js";

export const TERRAIN_CHUNK_RESOLUTION = STREAM_CHUNK_SIZE / TERRAIN_VERTEX_SPACING;

/** Spend spare CPU time on GPU-bound frames without changing terrain/quality budgets. */
export function terrainGenerationBudgetMs(frameMs = 16.7): number {
  return Math.max(2.5, Math.min(8, (Number.isFinite(frameMs) ? frameMs : 16.7) * 0.15));
}

export type WorldChunkLifecycle =
  | "REQUESTED"
  | "GENERATING"
  | "CPU_READY"
  | "GPU_READY"
  | "VISIBLE"
  | "EVICTABLE"
  | "FAILED";

interface CpuTerrainChunk {
  readonly terrain: MeshGeometry;
  readonly water: MeshGeometry | undefined;
  readonly centerY: number;
  readonly radius: number;
}

interface TerrainChunk {
  readonly x: number;
  readonly z: number;
  readonly id: string;
  state: WorldChunkLifecycle;
  builder: TerrainChunkGeometryBuilder | undefined;
  waterBuilder: RiverWaterGeometryBuilder | undefined;
  cpu: CpuTerrainChunk | undefined;
  terrainMesh: GpuMesh | undefined;
  waterMesh: GpuMesh | undefined;
  centerY: number;
  radius: number;
  generationMs: number;
  uploadMs: number;
  error: string | undefined;
  evictAfterUpdate: number;
}

export interface WorldStreamingSnapshot {
  readonly currentChunk: { readonly x: number; readonly z: number; readonly id: string };
  readonly loadedChunks: number;
  readonly readyActiveChunks: number;
  readonly visibleChunks: number;
  readonly pendingChunks: number;
  readonly cpuReadyChunks: number;
  readonly gpuReadyChunks: number;
  readonly evictableChunks: number;
  readonly failedChunks: number;
  readonly recoveredChunks: number;
  readonly lastError?: string;
  readonly generatedChunks: number;
  readonly evictedChunks: number;
  readonly generationMsAverage: number;
  readonly generationMsMax: number;
  readonly uploadMsAverage: number;
  readonly uploadMsMax: number;
  readonly terrainGpuBytes: number;
}

export interface TerrainChunkDebugRecord {
  readonly x: number;
  readonly z: number;
  readonly state: WorldChunkLifecycle;
}

/** Canonical 64 m streaming terrain. Asset meshes remain globally shared. */
export class HeightfieldTerrain {
  private readonly chunks = new Map<string, TerrainChunk>();
  private currentChunkX = 0;
  private currentChunkZ = 0;
  private updateIndex = 0;
  private visibleChunks = 0;
  private generatedChunks = 0;
  private evictedChunks = 0;
  private generationMsTotal = 0;
  private generationMsMax = 0;
  private uploadMsTotal = 0;
  private uploadMsMax = 0;
  private uploadedChunks = 0;
  private terrainGpuBytes = 0;
  private initialized = false;
  private centerRevision = 0;

  constructor(private readonly renderer: Renderer) {
    this.warmStart(0, 12);
  }

  get revision(): number {
    return this.centerRevision;
  }

  getHeightAt(x: number, z: number): number {
    return getWorldHeightAt(x, z);
  }

  update(playerX: number, playerZ: number, velocityX = 0, velocityZ = 0): void {
    this.updateIndex += 1;
    const center = worldChunkAt(playerX, playerZ);
    const changed = !this.initialized || center.x !== this.currentChunkX || center.z !== this.currentChunkZ;
    this.currentChunkX = center.x;
    this.currentChunkZ = center.z;
    if (changed) {
      this.initialized = true;
      this.centerRevision += 1;
      this.requestWindow(velocityX, velocityZ);
      this.markEvictions();
    }
    this.processEvictions();
    this.generateNextRequested();
    this.uploadNearestReady(2);
    this.refreshMetrics();
  }

  render(renderer: Renderer, camera: Camera, waterVisible = true): void {
    this.visibleChunks = 0;
    for (const chunk of this.chunks.values()) {
      if (chunk.state === "VISIBLE") chunk.state = "GPU_READY";
      if (!chunk.terrainMesh) continue;
      if (chunkDistance(chunk.x, chunk.z, this.currentChunkX, this.currentChunkZ) > TERRAIN_GPU_RADIUS) continue;
      const centerX = (chunk.x + 0.5) * STREAM_CHUNK_SIZE;
      const centerZ = (chunk.z + 0.5) * STREAM_CHUNK_SIZE;
      if (!camera.isSphereVisible(centerX, chunk.centerY, centerZ, chunk.radius)) continue;
      renderer.drawTerrain(chunk.terrainMesh);
      if (waterVisible && chunk.waterMesh) renderer.drawWater(chunk.waterMesh);
      chunk.state = "VISIBLE";
      this.visibleChunks += 1;
    }
  }

  /** Transparent surface follows opaque actors/props so submerged bodies remain visible through it. */
  renderWater(renderer: Renderer, camera: Camera): void {
    for (const chunk of this.chunks.values()) {
      if (!chunk.waterMesh || chunk.state !== "VISIBLE") continue;
      if (!camera.isSphereVisible((chunk.x + 0.5) * STREAM_CHUNK_SIZE, chunk.centerY, (chunk.z + 0.5) * STREAM_CHUNK_SIZE, chunk.radius)) continue;
      renderer.drawWater(chunk.waterMesh);
    }
  }

  activeChunkIds(radius = TERRAIN_GPU_RADIUS): readonly string[] {
    return prioritizedChunkWindow(this.currentChunkX, this.currentChunkZ, radius).map((chunk) => chunk.id);
  }

  activeChunkCoordinates(radius = TERRAIN_GPU_RADIUS): readonly { readonly x: number; readonly z: number }[] {
    return prioritizedChunkWindow(this.currentChunkX, this.currentChunkZ, radius);
  }

  debugChunks(): readonly TerrainChunkDebugRecord[] {
    return [...this.chunks.values()].map((chunk) => ({ x: chunk.x, z: chunk.z, state: chunk.state }));
  }

  snapshot(): WorldStreamingSnapshot {
    let readyActiveChunks = 0;
    let pendingChunks = 0;
    let cpuReadyChunks = 0;
    let gpuReadyChunks = 0;
    let evictableChunks = 0;
    let failedChunks = 0;
    let recoveredChunks = 0;
    let lastError: string | undefined;
    for (const chunk of this.chunks.values()) {
      if (
        chunk.terrainMesh &&
        chunkDistance(chunk.x, chunk.z, this.currentChunkX, this.currentChunkZ) <= TERRAIN_GPU_RADIUS
      ) readyActiveChunks += 1;
      if (chunk.state === "REQUESTED" || chunk.state === "GENERATING") pendingChunks += 1;
      else if (chunk.state === "CPU_READY") cpuReadyChunks += 1;
      else if (chunk.state === "GPU_READY" || chunk.state === "VISIBLE") gpuReadyChunks += 1;
      else if (chunk.state === "EVICTABLE") evictableChunks += 1;
      else if (chunk.state === "FAILED") failedChunks += 1;
      if (chunk.error) {
        lastError = `${chunk.id}: ${chunk.error}`;
        if (chunk.state !== "FAILED") recoveredChunks += 1;
      }
    }
    return {
      currentChunk: { x: this.currentChunkX, z: this.currentChunkZ, id: worldChunkId(this.currentChunkX, this.currentChunkZ) },
      loadedChunks: this.chunks.size,
      readyActiveChunks,
      visibleChunks: this.visibleChunks,
      pendingChunks,
      cpuReadyChunks,
      gpuReadyChunks,
      evictableChunks,
      failedChunks,
      recoveredChunks,
      ...(lastError ? { lastError } : {}),
      generatedChunks: this.generatedChunks,
      evictedChunks: this.evictedChunks,
      generationMsAverage: this.generatedChunks > 0 ? this.generationMsTotal / this.generatedChunks : 0,
      generationMsMax: this.generationMsMax,
      uploadMsAverage: this.uploadedChunks > 0 ? this.uploadMsTotal / this.uploadedChunks : 0,
      uploadMsMax: this.uploadMsMax,
      terrainGpuBytes: this.terrainGpuBytes,
    };
  }

  dispose(): void {
    for (const chunk of this.chunks.values()) this.releaseChunk(chunk);
    this.chunks.clear();
    this.refreshMetrics();
  }

  private warmStart(playerX: number, playerZ: number): void {
    const center = worldChunkAt(playerX, playerZ);
    this.currentChunkX = center.x;
    this.currentChunkZ = center.z;
    this.initialized = true;
    this.centerRevision = 1;
    this.requestWindow(0, 0);
    for (const coordinate of prioritizedChunkWindow(center.x, center.z, TERRAIN_GPU_RADIUS)) {
      const chunk = this.chunks.get(coordinate.id)!;
      this.generateChunk(chunk);
      this.uploadChunk(chunk);
    }
    this.refreshMetrics();
  }

  private requestWindow(velocityX: number, velocityZ: number): void {
    for (const coordinate of prioritizedChunkWindow(
      this.currentChunkX,
      this.currentChunkZ,
      CHUNK_PREFETCH_RADIUS,
      velocityX,
      velocityZ,
    )) {
      const existing = this.chunks.get(coordinate.id);
      if (existing) {
        if (existing.state === "EVICTABLE") {
          existing.state = existing.terrainMesh ? "GPU_READY" : existing.cpu ? "CPU_READY" : "REQUESTED";
        }
        existing.evictAfterUpdate = Number.POSITIVE_INFINITY;
        continue;
      }
      this.chunks.set(coordinate.id, {
        x: coordinate.x,
        z: coordinate.z,
        id: coordinate.id,
        state: "REQUESTED",
        builder: undefined,
        waterBuilder: undefined,
        cpu: undefined,
        terrainMesh: undefined,
        waterMesh: undefined,
        centerY: 0,
        radius: STREAM_CHUNK_SIZE,
        generationMs: 0,
        uploadMs: 0,
        error: undefined,
        evictAfterUpdate: Number.POSITIVE_INFINITY,
      });
    }
  }

  private markEvictions(): void {
    for (const chunk of this.chunks.values()) {
      if (chunkDistance(chunk.x, chunk.z, this.currentChunkX, this.currentChunkZ) <= CHUNK_EVICTION_RADIUS) continue;
      if (chunk.state !== "EVICTABLE") {
        chunk.state = "EVICTABLE";
        chunk.evictAfterUpdate = this.updateIndex + 1;
      }
    }
  }

  private processEvictions(): void {
    for (const [id, chunk] of this.chunks) {
      if (chunk.state !== "EVICTABLE" || chunk.evictAfterUpdate > this.updateIndex) continue;
      this.releaseChunk(chunk);
      this.chunks.delete(id);
      this.evictedChunks += 1;
    }
  }

  private generateNextRequested(): void {
    const priority = prioritizedChunkWindow(this.currentChunkX, this.currentChunkZ, CHUNK_PREFETCH_RADIUS)
      .map((coordinate) => this.chunks.get(coordinate.id))
      .filter((chunk): chunk is TerrainChunk => chunk !== undefined);
    const chunk = priority.find((candidate) => candidate.state === "GENERATING")
      ?? priority.find((candidate) => candidate.state === "REQUESTED");
    if (!chunk) return;
    if (chunk.state === "REQUESTED") {
      chunk.state = "GENERATING";
      chunk.builder = new TerrainChunkGeometryBuilder(chunk.x * STREAM_CHUNK_SIZE, chunk.z * STREAM_CHUNK_SIZE);
    }
    const startedAt = performance.now();
    const deadline = startedAt + terrainGenerationBudgetMs(this.renderer.metrics.frameMs);
    try {
      let terrainReady = false;
      // Each row is an atomic unit; check elapsed CPU between rows and stages.
      // Fixed rows/frame made a wet chunk take 67 frames even on a 3 FPS GPU.
      for (let row = 0; row < 32; row += 1) {
        terrainReady = chunk.builder!.step(1);
        if (terrainReady || performance.now() >= deadline) break;
      }
      if (!terrainReady || performance.now() >= deadline) {
        chunk.generationMs += performance.now() - startedAt;
        return;
      }
      const generated = chunk.builder!.finish();
      chunk.waterBuilder ??= new RiverWaterGeometryBuilder(chunk.x, chunk.z);
      let waterReady = false;
      for (let row = 0; row < 8 && performance.now() < deadline; row += 1) {
        waterReady = chunk.waterBuilder.step(1);
        if (waterReady) break;
      }
      if (!waterReady) {
        chunk.generationMs += performance.now() - startedAt;
        return;
      }
      chunk.cpu = cpuChunkFromGeometry(generated, chunk.waterBuilder.finish());
      chunk.builder = undefined;
      chunk.waterBuilder = undefined;
      chunk.centerY = chunk.cpu.centerY;
      chunk.radius = chunk.cpu.radius;
      chunk.state = "CPU_READY";
    } catch (error) {
      chunk.builder = undefined;
      chunk.waterBuilder = undefined;
      chunk.cpu = createFallbackTerrainChunkCpu(chunk.x, chunk.z);
      chunk.centerY = chunk.cpu.centerY;
      chunk.radius = chunk.cpu.radius;
      chunk.state = "CPU_READY";
      chunk.error = error instanceof Error ? error.message : "Unknown world chunk generation error";
    }
    chunk.generationMs += performance.now() - startedAt;
    this.recordGeneratedChunk(chunk);
  }

  private uploadNearestReady(limit: number): void {
    let uploaded = 0;
    for (const coordinate of prioritizedChunkWindow(this.currentChunkX, this.currentChunkZ, TERRAIN_GPU_RADIUS)) {
      const chunk = this.chunks.get(coordinate.id);
      if (!chunk || chunk.state !== "CPU_READY") continue;
      this.uploadChunk(chunk);
      uploaded += 1;
      if (uploaded >= limit) break;
    }
  }

  private generateChunk(chunk: TerrainChunk): void {
    if (chunk.state !== "REQUESTED") return;
    chunk.state = "GENERATING";
    const startedAt = performance.now();
    try {
      chunk.cpu = createTerrainChunkCpu(chunk.x, chunk.z);
      chunk.centerY = chunk.cpu.centerY;
      chunk.radius = chunk.cpu.radius;
      chunk.state = "CPU_READY";
    } catch (error) {
      chunk.cpu = createFallbackTerrainChunkCpu(chunk.x, chunk.z);
      chunk.centerY = chunk.cpu.centerY;
      chunk.radius = chunk.cpu.radius;
      chunk.state = "CPU_READY";
      chunk.error = error instanceof Error ? error.message : "Unknown world chunk generation error";
    }
    chunk.generationMs = performance.now() - startedAt;
    this.recordGeneratedChunk(chunk);
  }

  private uploadChunk(chunk: TerrainChunk): void {
    if (chunk.state !== "CPU_READY" || !chunk.cpu) return;
    const startedAt = performance.now();
    try {
      chunk.terrainMesh = this.renderer.createMesh(chunk.cpu.terrain);
      this.renderer.metrics.surfaceDataBytes += chunk.terrainMesh.surfaceDataBytes;
      if (chunk.cpu.water && chunk.cpu.water.indices.length > 0) chunk.waterMesh = this.renderer.createMesh(chunk.cpu.water);
      this.terrainGpuBytes += chunk.terrainMesh.allocatedBufferBytes + (chunk.waterMesh?.allocatedBufferBytes ?? 0);
      chunk.cpu = undefined;
      chunk.state = "GPU_READY";
    } catch (error) {
      chunk.state = "FAILED";
      chunk.error = error instanceof Error ? error.message : "Unknown world chunk GPU upload error";
    }
    chunk.uploadMs = performance.now() - startedAt;
    this.uploadedChunks += 1;
    this.uploadMsTotal += chunk.uploadMs;
    this.uploadMsMax = Math.max(this.uploadMsMax, chunk.uploadMs);
  }

  private releaseChunk(chunk: TerrainChunk): void {
    if (chunk.terrainMesh) {
      this.terrainGpuBytes = Math.max(0, this.terrainGpuBytes - chunk.terrainMesh.allocatedBufferBytes);
      this.renderer.destroyMesh(chunk.terrainMesh);
      chunk.terrainMesh = undefined;
    }
    if (chunk.waterMesh) {
      this.terrainGpuBytes = Math.max(0, this.terrainGpuBytes - chunk.waterMesh.allocatedBufferBytes);
      this.renderer.destroyMesh(chunk.waterMesh);
      chunk.waterMesh = undefined;
    }
    chunk.cpu = undefined;
    chunk.builder = undefined;
    chunk.waterBuilder = undefined;
  }

  private recordGeneratedChunk(chunk: TerrainChunk): void {
    this.generatedChunks += 1;
    this.generationMsTotal += chunk.generationMs;
    this.generationMsMax = Math.max(this.generationMsMax, chunk.generationMs);
  }

  private refreshMetrics(): void {
    let triangles = 0;
    let gpuChunks = 0;
    for (const chunk of this.chunks.values()) {
      if (!chunk.terrainMesh) continue;
      gpuChunks += 1;
      triangles += chunk.terrainMesh.triangleCount;
    }
    this.renderer.metrics.totalTerrainChunks = gpuChunks;
    this.renderer.metrics.totalTerrainTriangles = triangles;
  }
}

export function createTerrainChunkCpu(chunkX: number, chunkZ: number): CpuTerrainChunk {
  const generated = createChunkGeometry(chunkX * STREAM_CHUNK_SIZE, chunkZ * STREAM_CHUNK_SIZE);
  return cpuChunkFromGeometry(generated, createRiverWaterGeometry(chunkX, chunkZ));
}

function cpuChunkFromGeometry(
  generated: { geometry: MeshGeometry; minimumHeight: number; maximumHeight: number },
  water: MeshGeometry | undefined,
): CpuTerrainChunk {
  return {
    terrain: generated.geometry,
    water,
    centerY: (generated.minimumHeight + generated.maximumHeight) * 0.5,
    radius: Math.hypot(
      STREAM_CHUNK_SIZE * 0.5,
      STREAM_CHUNK_SIZE * 0.5,
      (generated.maximumHeight - generated.minimumHeight) * 0.5,
    ),
  };
}

export function createChunkGeometry(
  originX: number,
  originZ: number,
): { geometry: MeshGeometry; minimumHeight: number; maximumHeight: number } {
  const builder = new TerrainChunkGeometryBuilder(originX, originZ);
  while (!builder.step(Number.POSITIVE_INFINITY)) {
    // Synchronous path is used only for initial warm-up and focused tests.
  }
  return builder.finish();
}

const TERRAIN_HEIGHT_SAMPLE_STEP = TERRAIN_VERTEX_SPACING;
const TERRAIN_SURFACE_SAMPLE_STEP = TERRAIN_VERTEX_SPACING;
const TERRAIN_HEIGHT_CELLS = STREAM_CHUNK_SIZE / TERRAIN_HEIGHT_SAMPLE_STEP;
const TERRAIN_SURFACE_CELLS = STREAM_CHUNK_SIZE / TERRAIN_SURFACE_SAMPLE_STEP;
const TERRAIN_HEIGHT_HALO_ROW = TERRAIN_HEIGHT_CELLS + 3;
const TERRAIN_SURFACE_ROW = TERRAIN_SURFACE_CELLS + 1;

class TerrainChunkGeometryBuilder {
  private readonly coarseHeights = new Float64Array(TERRAIN_HEIGHT_HALO_ROW * TERRAIN_HEIGHT_HALO_ROW);
  private readonly coarseSurfaces = new Float64Array(TERRAIN_SURFACE_ROW * TERRAIN_SURFACE_ROW * 7);
  private nextHeightRow = 0;
  private nextSurfaceRow = 0;
  private finished: ReturnType<typeof buildGeometryFromCoarse> | undefined;

  constructor(private readonly originX: number, private readonly originZ: number) {}

  /** Returns true when CPU geometry is ready. */
  step(maximumRows: number): boolean {
    let remaining = maximumRows;
    while (remaining > 0 && this.nextHeightRow < TERRAIN_HEIGHT_HALO_ROW) {
      const z = this.nextHeightRow;
      for (let x = 0; x < TERRAIN_HEIGHT_HALO_ROW; x += 1) {
        this.coarseHeights[z * TERRAIN_HEIGHT_HALO_ROW + x] = getWorldHeightAt(
          this.originX + (x - 1) * TERRAIN_HEIGHT_SAMPLE_STEP,
          this.originZ + (z - 1) * TERRAIN_HEIGHT_SAMPLE_STEP,
        );
      }
      this.nextHeightRow += 1;
      remaining -= 1;
    }
    while (remaining > 0 && this.nextSurfaceRow < TERRAIN_SURFACE_ROW) {
      const z = this.nextSurfaceRow;
      for (let x = 0; x < TERRAIN_SURFACE_ROW; x += 1) {
        const weights = getWorldTerrainSurfaceWeights(
          this.originX + x * TERRAIN_SURFACE_SAMPLE_STEP,
          this.originZ + z * TERRAIN_SURFACE_SAMPLE_STEP,
        );
        const offset = (z * TERRAIN_SURFACE_ROW + x) * 7;
        for (let weight = 0; weight < weights.length; weight += 1) this.coarseSurfaces[offset + weight] = weights[weight]!;
      }
      this.nextSurfaceRow += 1;
      remaining -= 1;
    }
    if (this.nextSurfaceRow < TERRAIN_SURFACE_ROW) return false;
    this.finished ??= buildGeometryFromCoarse(this.originX, this.originZ, this.coarseHeights, this.coarseSurfaces);
    return true;
  }

  finish(): { geometry: MeshGeometry; minimumHeight: number; maximumHeight: number } {
    if (!this.finished) throw new Error("Terrain chunk geometry is not ready");
    return this.finished;
  }
}

function buildGeometryFromCoarse(
  originX: number,
  originZ: number,
  coarseHeights: Float64Array,
  coarseSurfaces: Float64Array,
): { geometry: MeshGeometry; minimumHeight: number; maximumHeight: number } {
  const vertexCount = (TERRAIN_CHUNK_RESOLUTION + 1) ** 2;
  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const indices = new Uint16Array(TERRAIN_CHUNK_RESOLUTION * TERRAIN_CHUNK_RESOLUTION * 6);
  const surfaceWeights = new Uint8Array(vertexCount * 8);
  const expandedRow = TERRAIN_CHUNK_RESOLUTION + 3;
  const expandedHeights = new Float64Array(expandedRow * expandedRow);
  for (let expandedZ = 0; expandedZ < expandedRow; expandedZ += 1) {
    for (let expandedX = 0; expandedX < expandedRow; expandedX += 1) {
      expandedHeights[expandedZ * expandedRow + expandedX] = interpolateCoarse(
        coarseHeights,
        TERRAIN_HEIGHT_HALO_ROW,
        (expandedX - 1) / TERRAIN_HEIGHT_SAMPLE_STEP + 1,
        (expandedZ - 1) / TERRAIN_HEIGHT_SAMPLE_STEP + 1,
      );
    }
  }
  let minimumHeight = Number.POSITIVE_INFINITY;
  let maximumHeight = Number.NEGATIVE_INFINITY;
  let vertex = 0;
  for (let z = 0; z <= TERRAIN_CHUNK_RESOLUTION; z += 1) {
    for (let x = 0; x <= TERRAIN_CHUNK_RESOLUTION; x += 1) {
      const worldX = originX + x * TERRAIN_VERTEX_SPACING;
      const worldZ = originZ + z * TERRAIN_VERTEX_SPACING;
      const expandedIndex = (z + 1) * expandedRow + x + 1;
      const height = expandedHeights[expandedIndex]!;
      const dx = expandedHeights[expandedIndex - 1]! - expandedHeights[expandedIndex + 1]!;
      const dz = expandedHeights[expandedIndex - expandedRow]! - expandedHeights[expandedIndex + expandedRow]!;
      const normalLength = Math.hypot(dx, TERRAIN_VERTEX_SPACING * 2, dz) || 1;
      minimumHeight = Math.min(minimumHeight, height);
      maximumHeight = Math.max(maximumHeight, height);
      const positionOffset = vertex * 3;
      positions[positionOffset] = worldX;
      positions[positionOffset + 1] = height;
      positions[positionOffset + 2] = worldZ;
      normals[positionOffset] = dx / normalLength;
      normals[positionOffset + 1] = (TERRAIN_VERTEX_SPACING * 2) / normalLength;
      normals[positionOffset + 2] = dz / normalLength;
      const surfaceOffset = vertex * 8;
      for (let weight = 0; weight < 7; weight += 1) {
        const value = interpolateCoarseChannel(
          coarseSurfaces,
          TERRAIN_SURFACE_ROW,
          7,
          x / TERRAIN_SURFACE_SAMPLE_STEP,
          z / TERRAIN_SURFACE_SAMPLE_STEP,
          weight,
        );
        surfaceWeights[surfaceOffset + weight] = Math.round(Math.max(0, Math.min(1, value)) * 255);
      }
      vertex += 1;
    }
  }
  const row = TERRAIN_CHUNK_RESOLUTION + 1;
  let indexOffset = 0;
  for (let z = 0; z < TERRAIN_CHUNK_RESOLUTION; z += 1) {
    for (let x = 0; x < TERRAIN_CHUNK_RESOLUTION; x += 1) {
      const index = z * row + x;
      indices[indexOffset++] = index;
      indices[indexOffset++] = index + row;
      indices[indexOffset++] = index + 1;
      indices[indexOffset++] = index + 1;
      indices[indexOffset++] = index + row;
      indices[indexOffset++] = index + row + 1;
    }
  }
  return { geometry: { positions, normals, indices, surfaceWeights }, minimumHeight, maximumHeight };
}

function interpolateCoarse(data: Float64Array, row: number, x: number, z: number): number {
  return interpolateCoarseChannel(data, row, 1, x, z, 0);
}

function interpolateCoarseChannel(
  data: Float64Array,
  row: number,
  channels: number,
  x: number,
  z: number,
  channel: number,
): number {
  const x0 = Math.max(0, Math.min(row - 1, Math.floor(x)));
  const z0 = Math.max(0, Math.min(row - 1, Math.floor(z)));
  const x1 = Math.min(row - 1, x0 + 1);
  const z1 = Math.min(row - 1, z0 + 1);
  const localX = x - Math.floor(x);
  const localZ = z - Math.floor(z);
  const north = data[(z0 * row + x0) * channels + channel]! * (1 - localX) + data[(z0 * row + x1) * channels + channel]! * localX;
  const south = data[(z1 * row + x0) * channels + channel]! * (1 - localX) + data[(z1 * row + x1) * channels + channel]! * localX;
  return north * (1 - localZ) + south * localZ;
}

export function createRiverWaterGeometry(chunkX: number, chunkZ: number): MeshGeometry | undefined {
  const builder = new RiverWaterGeometryBuilder(chunkX, chunkZ);
  builder.step(Number.POSITIVE_INFINITY);
  return builder.finish();
}

/** Streamed chunks advance rows inside the shared CPU slice; warm-start may finish synchronously. */
export class RiverWaterGeometryBuilder {
  private readonly data: {
    originX: number;
    samples: ReturnType<typeof getWorldRiverProfileAt>[];
    minimumX: number;
    columns: number;
    positions: Float32Array;
    normals: Float32Array;
    texcoords: Float32Array;
  } | undefined;
  private nextRow = 0;
  private ready = false;
  private geometry: MeshGeometry | undefined;
  // Chunk-bounded canonical samples include the normal halo. Neighbor vertices
  // reuse them, so depth and mean-slope generation do not resample terrain five times.
  private readonly waterSamples = new Map<string, ReturnType<typeof sampleWorldWaterAt>>();

  constructor(chunkX: number, chunkZ: number) {
    const originX = chunkX * STREAM_CHUNK_SIZE;
    const originZ = chunkZ * STREAM_CHUNK_SIZE;
    // Envelope rejection needs only course geometry, not an expensive water mean.
    const samples = Array.from({ length: STREAM_CHUNK_SIZE + 1 }, (_, index) => getWorldRiverProfileAt(originX, originZ + index));
    if (samples.every((sample) => sample.x + sample.width * WORLD_WATER_HALF_WIDTH_FACTOR < originX || sample.x - sample.width * WORLD_WATER_HALF_WIDTH_FACTOR > originX + STREAM_CHUNK_SIZE)) {
      this.ready = true;
      return;
    }
    const minimumX = Math.max(originX, Math.floor(Math.min(...samples.map((sample) => sample.x - sample.width * WORLD_WATER_HALF_WIDTH_FACTOR)) * 2) / 2);
    const maximumX = Math.min(originX + STREAM_CHUNK_SIZE, Math.ceil(Math.max(...samples.map((sample) => sample.x + sample.width * WORLD_WATER_HALF_WIDTH_FACTOR)) * 2) / 2);
    const columns = Math.round((maximumX - minimumX) * 2) + 1;
    const positions = new Float32Array(samples.length * columns * 3);
    this.data = { originX, samples, minimumX, columns, positions, normals: new Float32Array(positions.length), texcoords: new Float32Array(samples.length * columns * 2) };
  }

  step(maximumRows: number): boolean {
    if (this.ready) return true;
    const { originX, samples, minimumX, columns, positions, normals, texcoords } = this.data!;
    let remaining = maximumRows;
    while (remaining > 0 && this.nextRow < samples.length) {
      const row = this.nextRow;
      const sample = samples[row]!;
      const left = Math.max(originX, Math.min(originX + STREAM_CHUNK_SIZE, sample.x - sample.width * WORLD_WATER_HALF_WIDTH_FACTOR));
      const right = Math.max(originX, Math.min(originX + STREAM_CHUNK_SIZE, sample.x + sample.width * WORLD_WATER_HALF_WIDTH_FACTOR));
      for (let column = 0; column < columns; column += 1) {
        // Global half-meter lattice and world-space wave phases match on both axes.
        const x = Math.max(left, Math.min(right, minimumX + column * 0.5));
        const z = sample.z;
        const water = this.sample(x, z);
        const vertex = row * columns + column;
        const offset = vertex * 3;
        positions[offset] = x;
        positions[offset + 1] = water.surfaceY;
        positions[offset + 2] = z;
        // Use the same canonical tapered mean, including the far-world fringe.
        const dx = this.sample(x - 0.5, z).surfaceY - this.sample(x + 0.5, z).surfaceY;
        const dz = (this.sample(x, z - 1).surfaceY - this.sample(x, z + 1).surfaceY) * 0.5;
        const inverseLength = 1 / Math.hypot(dx, 1, dz);
        normals[offset] = dx * inverseLength;
        normals[offset + 1] = inverseLength;
        normals[offset + 2] = dz * inverseLength;
        // Signed depth avoids filling dry triangles when interpolating across a bank.
        texcoords[vertex * 2] = water.surfaceY - water.terrainY;
        texcoords[vertex * 2 + 1] = water.distanceFromBank;
      }
      this.nextRow += 1;
      remaining -= 1;
    }
    if (this.nextRow < samples.length) return false;
    const indices: number[] = [];
    for (let row = 0; row < samples.length - 1; row += 1) for (let column = 0; column < columns - 1; column += 1) {
      const a = row * columns + column;
      const b = a + columns;
      const c = a + 1;
      const d = b + 1;
      // Drop collapsed envelope cells and fully dry cells; no invisible river-wide overdraw.
      if (Math.max(texcoords[a * 2]!, texcoords[b * 2]!, texcoords[c * 2]!, texcoords[d * 2]!) <= 0) continue;
      if (positions[c * 3]! > positions[a * 3]! + 1e-5) indices.push(a, b, c);
      if (positions[d * 3]! > positions[b * 3]! + 1e-5) indices.push(c, b, d);
    }
    this.geometry = indices.length > 0 ? { positions, normals, texcoords, indices: new Uint16Array(indices) } : undefined;
    this.waterSamples.clear();
    this.ready = true;
    return true;
  }

  private sample(x: number, z: number): ReturnType<typeof sampleWorldWaterAt> {
    const key = `${x}:${z}`;
    const cached = this.waterSamples.get(key);
    if (cached) return cached;
    const value = sampleWorldWaterAt(x, z);
    this.waterSamples.set(key, value);
    return value;
  }

  finish(): MeshGeometry | undefined {
    if (!this.ready) throw new Error("Water geometry is not ready");
    return this.geometry;
  }
}

function createFallbackTerrainChunkCpu(chunkX: number, chunkZ: number): CpuTerrainChunk {
  const originX = chunkX * STREAM_CHUNK_SIZE;
  const originZ = chunkZ * STREAM_CHUNK_SIZE;
  const positions = new Float32Array([
    originX, 0, originZ,
    originX, 0, originZ + STREAM_CHUNK_SIZE,
    originX + STREAM_CHUNK_SIZE, 0, originZ,
    originX + STREAM_CHUNK_SIZE, 0, originZ + STREAM_CHUNK_SIZE,
  ]);
  const surfaceWeights = new Uint8Array(32);
  for (let offset = 0; offset < surfaceWeights.length; offset += 8) surfaceWeights[offset] = 255;
  return {
    terrain: {
      positions,
      normals: new Float32Array([0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0]),
      indices: new Uint16Array([0, 1, 2, 2, 1, 3]),
      surfaceWeights,
    },
    water: undefined,
    centerY: 0,
    radius: Math.SQRT2 * STREAM_CHUNK_SIZE * 0.5,
  };
}
